import {
    DepositStatus,
    GeneralInviteTag,
    isGeneralInviteTag,
} from './inviteTags';
import { PLANNED_LITTERS, normalizeLitterName } from './plannedLitters';
import { ApplicationFieldRow } from './applicationDisplay';
import { ApplicationDraft } from './applicationFromRecord';
import { fillMissingFromDraft } from './fillMissingApplication';

export type InviteApplication = {
    fileName: string;
    uploadedAt: string;
    summary?: string;
    fields?: ApplicationFieldRow[];
};

export type OwnerInvite = {
    id: string;
    ownerName: string;
    email: string;
    phone: string;
    notes: string;
    litter: string;
    tags: GeneralInviteTag[];
    depositStatus: DepositStatus;
    createdAt: string;
    portalInvitedAt?: string;
    application?: InviteApplication;
};

export type OwnerInviteInput = {
    ownerName: string;
    email: string;
    phone?: string;
    notes?: string;
    litter?: string;
};

const STORAGE_KEY = 'galia-admin-invites';

export const toInvite = (value: Partial<OwnerInvite>): OwnerInvite | null => {
    if (!value || !value.email) {
        return null;
    }

    return {
        id: value.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ownerName: (value.ownerName || '').trim(),
        email: value.email.trim().toLowerCase(),
        phone: (value.phone || '').trim(),
        notes: (value.notes || '').trim(),
        litter: (value.litter || '').trim(),
        tags: Array.isArray(value.tags)
            ? value.tags.filter((tag): tag is GeneralInviteTag => isGeneralInviteTag(tag))
            : [],
        depositStatus:
            value.depositStatus === 'pending' || value.depositStatus === 'paid'
                ? value.depositStatus
                : '',
        createdAt: value.createdAt || new Date().toISOString(),
        portalInvitedAt:
            value.portalInvitedAt === undefined || value.portalInvitedAt === null
                ? undefined
                : String(value.portalInvitedAt),
        application:
            value.application && (value.application.fileName || value.application.summary)
                ? {
                      fileName: value.application.fileName || 'application.pdf',
                      uploadedAt: value.application.uploadedAt || new Date().toISOString(),
                      summary: value.application.summary || '',
                      fields: Array.isArray(value.application.fields)
                          ? value.application.fields
                          : undefined,
                  }
                : undefined,
    };
};

const readInvites = (): OwnerInvite[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw) as Partial<OwnerInvite>[];
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed
            .map((item) => toInvite(item))
            .filter((item): item is OwnerInvite => Boolean(item));
    } catch {
        return [];
    }
};

const writeInvites = (invites: OwnerInvite[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(invites));
};

export const sortInvites = (invites: OwnerInvite[]): OwnerInvite[] =>
    invites.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt));

export const listOwnerInvites = (): OwnerInvite[] => sortInvites(readInvites());

export const selectUnassignedInvites = (invites: OwnerInvite[]): OwnerInvite[] =>
    invites.filter((invite) => !invite.litter);

export const listUnassignedInvites = (): OwnerInvite[] =>
    selectUnassignedInvites(listOwnerInvites());

export const selectInboxInvites = (invites: OwnerInvite[]): OwnerInvite[] =>
    selectUnassignedInvites(invites);

export const listInboxInvites = (): OwnerInvite[] =>
    selectInboxInvites(listOwnerInvites());

const updateInvite = (
    id: string,
    updater: (invite: OwnerInvite) => OwnerInvite
): OwnerInvite | null => {
    let updated: OwnerInvite | null = null;
    const next = readInvites().map((invite) => {
        if (invite.id !== id) {
            return invite;
        }
        updated = updater(invite);
        return updated;
    });
    writeInvites(next);
    return updated;
};

export type LitterOwnerGroup = {
    litter: string;
    owners: OwnerInvite[];
};

export const selectInvitesByLitter = (invites: OwnerInvite[]): LitterOwnerGroup[] => {
    const groups = new Map<string, OwnerInvite[]>();

    invites.forEach((invite) => {
        if (!invite.litter) {
            return;
        }
        const existing = groups.get(invite.litter) ?? [];
        existing.push(invite);
        groups.set(invite.litter, existing);
    });

    return Array.from(groups.entries())
        .map(([litter, owners]) => ({
            litter,
            owners: owners.slice().sort((a, b) => a.ownerName.localeCompare(b.ownerName)),
        }))
        .sort((a, b) => {
            const latestA = a.owners.reduce(
                (latest, owner) => (owner.createdAt > latest ? owner.createdAt : latest),
                ''
            );
            const latestB = b.owners.reduce(
                (latest, owner) => (owner.createdAt > latest ? owner.createdAt : latest),
                ''
            );
            return latestB.localeCompare(latestA);
        });
};

export const listInvitesByLitter = (): LitterOwnerGroup[] =>
    selectInvitesByLitter(listOwnerInvites());

export const addOwnerInvite = (input: OwnerInviteInput): OwnerInvite => {
    const invite = toInvite({
        ...input,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        portalInvitedAt: '',
    });

    if (!invite) {
        throw new Error('A valid email is required.');
    }

    writeInvites([invite, ...readInvites()]);
    return invite;
};

export const upsertApplicationInvite = (
    draft: ApplicationDraft
): { invite: OwnerInvite; created: boolean; skipped: boolean } => {
    const existing = readInvites().find((invite) => invite.email === draft.email);
    const application = {
        fileName: draft.sourceLabel,
        uploadedAt: new Date().toISOString(),
        summary: draft.summary,
        fields: draft.fields,
    };

    if (existing) {
        const { next, changed } = fillMissingFromDraft(existing, draft);
        if (!changed) {
            return { invite: existing, created: false, skipped: true };
        }
        const invite = updateInvite(existing.id, (current) => ({
            ...current,
            ownerName: next.ownerName,
            phone: next.phone,
            tags: next.tags,
            application: next.application
                ? {
                      fileName: next.application.fileName || 'Jotform CSV',
                      uploadedAt: next.application.uploadedAt || new Date().toISOString(),
                      summary: next.application.summary || '',
                      fields: next.application.fields,
                  }
                : current.application,
        }));
        if (!invite) {
            throw new Error('Could not update that application card.');
        }
        return { invite, created: false, skipped: false };
    }

    const invite = toInvite({
        ownerName: draft.ownerName,
        email: draft.email,
        phone: draft.phone,
        notes: '',
        litter: '',
        tags: ['puppy-application'],
        createdAt: draft.createdAt || new Date().toISOString(),
        portalInvitedAt: '',
        application,
    });
    if (!invite) {
        throw new Error('A valid email is required.');
    }
    writeInvites([invite, ...readInvites()]);
    return { invite, created: true, skipped: false };
};

export const markPortalInvited = (id: string): OwnerInvite | null =>
    updateInvite(id, (invite) => ({
        ...invite,
        portalInvitedAt: new Date().toISOString(),
    }));

export const assignInviteToLitter = (id: string, litterName: string): OwnerInvite | null => {
    const trimmedLitter = litterName.trim();
    if (!trimmedLitter) {
        return null;
    }
    return updateInvite(id, (invite) => ({ ...invite, litter: trimmedLitter }));
};

export const unassignInvite = (id: string): OwnerInvite | null =>
    updateInvite(id, (invite) => ({ ...invite, litter: '' }));

export const deleteOwnerInvite = (id: string): boolean => {
    const current = readInvites();
    const next = current.filter((invite) => invite.id !== id);
    if (next.length === current.length) {
        return false;
    }
    writeInvites(next);
    return true;
};

export const setInviteTag = (id: string, tag: GeneralInviteTag): OwnerInvite | null =>
    updateInvite(id, (invite) => ({
        ...invite,
        tags: [tag],
    }));

export const clearInviteTags = (id: string): OwnerInvite | null =>
    updateInvite(id, (invite) => ({
        ...invite,
        tags: [],
    }));

export const setInviteDepositStatus = (
    id: string,
    status: Exclude<DepositStatus, ''>
): OwnerInvite | null =>
    updateInvite(id, (invite) => ({
        ...invite,
        depositStatus: status,
    }));

export const clearInviteDepositStatus = (id: string): OwnerInvite | null =>
    updateInvite(id, (invite) => ({
        ...invite,
        depositStatus: '',
    }));

export const formatInviteDate = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

export const selectOwnersForLitter = (
    invites: OwnerInvite[],
    litterName: string
): OwnerInvite[] => {
    const normalized = normalizeLitterName(litterName);
    return invites
        .filter((invite) => normalizeLitterName(invite.litter) === normalized)
        .sort((a, b) => a.ownerName.localeCompare(b.ownerName));
};

export const listOwnersForLitter = (litterName: string): OwnerInvite[] =>
    selectOwnersForLitter(listOwnerInvites(), litterName);

export const selectUnplannedLitterGroups = (invites: OwnerInvite[]): LitterOwnerGroup[] => {
    const planned = new Set(
        PLANNED_LITTERS.map((litter) => normalizeLitterName(litter.name))
    );
    return selectInvitesByLitter(invites).filter(
        (group) => !planned.has(normalizeLitterName(group.litter))
    );
};

export const listUnplannedLitterGroups = (): LitterOwnerGroup[] =>
    selectUnplannedLitterGroups(listOwnerInvites());

export const hasInviteForEmail = (email: string): boolean => {
    const normalized = email.trim().toLowerCase();
    return readInvites().some((invite) => invite.email === normalized);
};

export const hasPortalInvite = (
    invite: Pick<OwnerInvite, 'portalInvitedAt'> | null | undefined
): boolean => {
    if (!invite) {
        return false;
    }
    if (invite.portalInvitedAt === undefined || invite.portalInvitedAt === null) {
        return true;
    }
    return String(invite.portalInvitedAt).trim().length > 0;
};

export const hasPortalInviteForEmail = (email: string): boolean => {
    const normalized = email.trim().toLowerCase();
    return readInvites().some(
        (invite) => invite.email === normalized && hasPortalInvite(invite)
    );
};
